/**
 * LCVFR Run Console — debug panel showing recent workflow runs.
 *
 * Displays run status, timing, node count, and errors.
 * Updates in real-time as the LCVFR pipeline processes.
 */

import { useLcvfrRecentRuns, useLcvfrPipelineActive } from '@/lib/hooks/useLcvfrArtifact';
import type { LcvfrRunSummary } from '@/lib/stores/lcvfr-store';
import { PanelSkeleton } from '@/components/ui/SkeletonLoader';

// ─────────────────────────────────────────────────────────────────────────────
// Status Colors
// ─────────────────────────────────────────────────────────────────────────────

const statusConfig: Record<string, { dot: string; bg: string; text: string; label: string }> = {
  succeeded: { dot: 'bg-emerald-500', bg: 'bg-emerald-50',  text: 'text-emerald-700', label: 'OK' },
  failed:    { dot: 'bg-red-500',     bg: 'bg-red-50',      text: 'text-red-600',     label: 'FAIL' },
  running:   { dot: 'bg-teal-500',    bg: 'bg-teal-50',     text: 'text-teal-700',    label: 'RUN' },
  queued:    { dot: 'bg-slate-400',   bg: 'bg-slate-100',   text: 'text-slate-500',   label: 'WAIT' },
  canceled:  { dot: 'bg-amber-500',   bg: 'bg-amber-50',    text: 'text-amber-700',   label: 'STOP' },
};

// ─────────────────────────────────────────────────────────────────────────────
// Run Row
// ─────────────────────────────────────────────────────────────────────────────

function RunRow({ run, index }: { run: LcvfrRunSummary; index: number }): JSX.Element {
  const config = statusConfig[run.status] ?? statusConfig.queued!;
  const timeAgo = formatTimeAgo(run.finishedAt);
  const isRunning = run.status === 'running';

  const staggerClass =
    index === 0 ? 'animate-stagger-1'
    : index === 1 ? 'animate-stagger-2'
    : index === 2 ? 'animate-stagger-3'
    : index === 3 ? 'animate-stagger-4'
    : 'animate-stagger-5';

  return (
    <div className={`group rounded-lg px-4 py-3 transition-all duration-200 hover:bg-slate-50/80 ${staggerClass}`}>
      <div className="flex items-center gap-3">
        {/* ── Status badge pill with dot ── */}
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-2xs font-bold uppercase tracking-wider ${config.bg} ${config.text}`}
          style={{ border: '1px solid currentColor', borderColor: 'transparent' }}
        >
          <span
            className={`inline-block h-1.5 w-1.5 rounded-full ${config.dot} ${
              isRunning ? 'animate-pulse-glow' : ''
            }`}
          />
          {config.label}
        </span>

        {/* ── Run info ── */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5">
            <span className="truncate font-mono text-xs font-medium text-slate-600 tabular-nums">
              {run.runId.slice(0, 12)}
            </span>

            {/* ── Node & artifact metric boxes ── */}
            <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-2xs font-medium text-slate-500">
              <svg className="h-3 w-3 text-slate-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
              </svg>
              <span className="tabular-nums">{run.nodeCount}</span>
            </span>

            {run.artifactsWritten > 0 && (
              <span className="inline-flex items-center gap-1 rounded-md bg-teal-50 px-1.5 py-0.5 text-2xs font-medium text-teal-600">
                <svg className="h-3 w-3 text-teal-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
                <span className="tabular-nums">{run.artifactsWritten}</span>
              </span>
            )}
          </div>

          {run.error && (
            <p className="mt-1 truncate font-mono text-2xs text-red-500" title={run.error}>
              {run.error}
            </p>
          )}
        </div>

        {/* ── Duration metric ── */}
        <div className="flex flex-col items-end gap-0.5">
          <span className="font-mono text-xs font-semibold text-slate-700 tabular-nums">
            {formatDuration(run.totalDurationMs)}
          </span>
          <span className="font-mono text-2xs text-slate-400 tabular-nums">{timeAgo}</span>
        </div>
      </div>

      {/* ── Progress bar for running state ── */}
      {isRunning && (
        <div className="mt-2.5 h-1 w-full overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full rounded-full animate-shimmer"
            style={{
              background: 'linear-gradient(90deg, #189189 0%, #22b5a8 50%, #189189 100%)',
              backgroundSize: '200% 100%',
              width: '60%',
            }}
          />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Run Console
// ─────────────────────────────────────────────────────────────────────────────

interface RunConsoleProps {
  loading?: boolean;
  maxVisible?: number;
}

export function RunConsole({ loading, maxVisible = 15 }: RunConsoleProps): JSX.Element {
  const runs = useLcvfrRecentRuns();
  const pipelineActive = useLcvfrPipelineActive();

  if (loading) {
    return <PanelSkeleton />;
  }

  const visibleRuns = runs.slice(0, maxVisible);
  const succeededCount = runs.filter((r) => r.status === 'succeeded').length;
  const failedCount = runs.filter((r) => r.status === 'failed').length;

  return (
    <div className="card animate-fade-in">
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-5 py-4">
        <div className="flex items-center gap-2.5">
          <h3 className="section-header">Run Console</h3>
          {pipelineActive && (
            <span className="badge-teal flex items-center gap-1.5 text-2xs">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-teal-500 animate-pulse-glow" />
              Active
            </span>
          )}
        </div>
        {runs.length > 0 && (
          <div className="flex items-center gap-2">
            {/* ── Summary metric pills ── */}
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-2xs font-semibold text-emerald-700" style={{ border: '1px solid rgba(34,197,94,0.12)' }}>
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
              <span className="tabular-nums">{succeededCount}</span>
            </span>
            {failedCount > 0 && (
              <span className="badge-red flex items-center gap-1 text-2xs font-semibold">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500" />
                <span className="tabular-nums">{failedCount}</span>
              </span>
            )}
            <span className="font-mono text-2xs text-slate-400 tabular-nums">
              {runs.length} total
            </span>
          </div>
        )}
      </div>

      <div className="divider-gradient" />

      {/* ── Body ── */}
      <div className="px-2 py-3">
        {visibleRuns.length > 0 ? (
          <div className="space-y-0.5">
            {visibleRuns.map((run, index) => (
              <RunRow key={run.runId} run={run} index={index} />
            ))}
            {runs.length > maxVisible && (
              <div className="pt-3 text-center">
                <span className="font-mono text-2xs text-slate-400 tabular-nums">
                  + {runs.length - maxVisible} more runs
                </span>
              </div>
            )}
          </div>
        ) : (
          /* ── Empty state ── */
          <div className="empty-state animate-scale-in">
            <div className="empty-state-icon">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z" />
              </svg>
            </div>
            <p className="empty-state-title">No runs yet</p>
            <p className="empty-state-desc">Runs appear when the LCVFR pipeline processes events</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatTimeAgo(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}
